import api from '../../services/api';

class UserPreferencesService {
  constructor() {
    this.apiBaseUrl = '/api';
    this.isApiAvailable = false;
    this.checkApiAvailability();
  }

  /**
   * Verifica se a API está disponível
   */
  async checkApiAvailability() {
    try {
      const response = await api.get('/health');
      this.isApiAvailable = response.status === 200;
      // API disponível/indisponível
    } catch (error) {
      this.isApiAvailable = false;
      // API indisponível (fallback para localStorage)
    }
  }

  /**
   * Obter preferências do usuário
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @returns {Promise<Object|null>} Preferências do usuário
   */
  async getPreferences(userId = null) {
    try {
      // Tenta API primeiro, independente do flag isApiAvailable
      const response = await api.get('/user/preferences');
      if (response.data?.success) {
        // Preferências carregadas da API
        return response.data.data;
      }
      
      // Fallback para localStorage
      return this.getPreferencesFromLocalStorage(userId);
    } catch (error) {
      // Erro ao carregar preferências da API, usando localStorage
      return this.getPreferencesFromLocalStorage(userId);
    }
  }

  /**
   * Salvar preferências do usuário
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @param {Object} preferences - Preferências a serem salvas
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async savePreferences(userId = null, preferences) {
    try {
      if (this.isApiAvailable) {
        // Usar API do backend
        const response = await api.post('/user/preferences', preferences);
        if (response.data.success) {
          // Preferências salvas na API
          return true;
        }
      }
      
      // Fallback para localStorage
      return this.savePreferencesToLocalStorage(userId, preferences);
    } catch (error) {
      // Erro ao salvar preferências na API, usando localStorage
      return this.savePreferencesToLocalStorage(userId, preferences);
    }
  }

  /**
   * Atualizar apenas o tema (otimizado para performance)
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @param {string} themeMode - Modo do tema
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async updateTheme(userId = null, themeMode) {
    try {
      if (this.isApiAvailable) {
        // Usar API específica para tema (mais rápida)
        const response = await api.patch('/user/preferences/theme', { 
          theme_mode: themeMode 
        }, {
          timeout: 3000 // Timeout reduzido para 3 segundos
        });
        
        if (response.data.success) {
          // Tema atualizado na API
          return true;
        }
      }
      
      // Fallback para localStorage (instantâneo)
      return this.updateThemeInLocalStorage(userId, themeMode);
    } catch (error) {
      // Erro ao atualizar tema na API, usando localStorage
      // Sempre retorna true para localStorage (nunca falha)
      return this.updateThemeInLocalStorage(userId, themeMode);
    }
  }

  /**
   * Obter preferências do localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @returns {Object|null} Preferências do usuário
   */
  getPreferencesFromLocalStorage(userId) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const preferences = JSON.parse(stored);
        // Preferências carregadas do localStorage
        return preferences;
      }
      
      return null;
    } catch (error) {
      // Erro ao carregar preferências do localStorage
      return null;
    }
  }

  /**
   * Salvar preferências no localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @param {Object} preferences - Preferências a serem salvas
   * @returns {boolean} Sucesso da operação
   */
  savePreferencesToLocalStorage(userId, preferences) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const dataToSave = {
        ...preferences,
        userId,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      // Preferências salvas no localStorage
      return true;
    } catch (error) {
      // Erro ao salvar preferências no localStorage
      return false;
    }
  }

  /**
   * Atualizar tema no localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @param {string} themeMode - Modo do tema
   * @returns {boolean} Sucesso da operação
   */
  updateThemeInLocalStorage(userId, themeMode) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const existing = this.getPreferencesFromLocalStorage(userId) || {};
      
      const updatedPreferences = {
        ...existing,
        theme_mode: themeMode,
        userId,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem(storageKey, JSON.stringify(updatedPreferences));
      // Tema atualizado no localStorage
      return true;
    } catch (error) {
      // Erro ao atualizar tema no localStorage
      return false;
    }
  }

  /**
   * Verificar se a API está disponível
   * @returns {boolean} Status da API
   */
  getApiStatus() {
    return this.isApiAvailable;
  }

  /**
   * Forçar verificação da API
   * @returns {Promise<boolean>} Status da API
   */
  async refreshApiStatus() {
    await this.checkApiAvailability();
    return this.isApiAvailable;
  }

  /**
   * Salvar foto do usuário
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @param {string} photoBase64 - Foto em base64
   * @param {Object} metadata - Metadados da foto
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async saveUserPhoto(userId = null, photoBase64, metadata = {}) {
    try {
      // Sempre tenta API primeiro
      const response = await api.post('/user/preferences/photo', {
        user_photo: photoBase64,
        photo_metadata: metadata
      });
      if (response.data?.success) {
        // Foto salva na API
        return true;
      }
      
      // Fallback para localStorage
      return this.saveUserPhotoToLocalStorage(userId, photoBase64, metadata);
    } catch (error) {
      // Erro ao salvar foto na API, usando localStorage
      return this.saveUserPhotoToLocalStorage(userId, photoBase64, metadata);
    }
  }

  /**
   * Remover foto do usuário
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async removeUserPhoto(userId = null) {
    try {
      if (this.isApiAvailable) {
        const response = await api.delete('/user/preferences/photo');
        if (response.data.success) {
          // Foto removida da API
          return true;
        }
      }
      
      // Fallback para localStorage
      return this.removeUserPhotoFromLocalStorage(userId);
    } catch (error) {
      // Erro ao remover foto da API, usando localStorage
      return this.removeUserPhotoFromLocalStorage(userId);
    }
  }

  /**
   * Resetar preferências para padrão
   * @param {string} userId - ID do usuário (opcional, será obtido do token)
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async resetToDefault(userId = null) {
    try {
      if (this.isApiAvailable) {
        const response = await api.post('/user/preferences/reset');
        if (response.data.success) {
          // Preferências resetadas na API
          return true;
        }
      }
      
      // Fallback para localStorage
      return this.resetToDefaultInLocalStorage(userId);
    } catch (error) {
      // Erro ao resetar preferências na API, usando localStorage
      return this.resetToDefaultInLocalStorage(userId);
    }
  }

  /**
   * Obter estatísticas das preferências
   * @returns {Promise<Object>} Estatísticas
   */
  async getStats() {
    try {
      if (this.isApiAvailable) {
        const response = await api.get('/user/preferences/stats');
        if (response.data.success) {
          return response.data.data;
        }
      }
      
      // Fallback: estatísticas do localStorage
      return this.getLocalStorageStats();
    } catch (error) {
      // Erro ao obter estatísticas da API
      return this.getLocalStorageStats();
    }
  }

  /**
   * Obter estatísticas do localStorage
   * @returns {Object} Estatísticas
   */
  getLocalStorageStats() {
    try {
      const stats = {
        totalUsers: 0,
        themeDistribution: {},
        source: 'localStorage'
      };

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('user_preferences_')) {
          const preferences = this.getPreferencesFromLocalStorage(key.replace('user_preferences_', ''));
          if (preferences) {
            stats.totalUsers++;
            const theme = preferences.theme_mode || 'default';
            stats.themeDistribution[theme] = (stats.themeDistribution[theme] || 0) + 1;
          }
        }
      }

      return stats;
    } catch (error) {
      // Erro ao obter estatísticas do localStorage
      return { totalUsers: 0, themeDistribution: {}, source: 'localStorage' };
    }
  }

  /**
   * Salvar foto do usuário no localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @param {string} photoBase64 - Foto em base64
   * @param {Object} metadata - Metadados da foto
   * @returns {boolean} Sucesso da operação
   */
  saveUserPhotoToLocalStorage(userId, photoBase64, metadata = {}) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const existing = this.getPreferencesFromLocalStorage(userId) || {};
      
      const updatedPreferences = {
        ...existing,
        user_photo: photoBase64,
        photo_metadata: metadata,
        userId,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem(storageKey, JSON.stringify(updatedPreferences));
      // Foto salva no localStorage
      return true;
    } catch (error) {
      // Erro ao salvar foto no localStorage
      return false;
    }
  }

  /**
   * Remover foto do usuário do localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @returns {boolean} Sucesso da operação
   */
  removeUserPhotoFromLocalStorage(userId) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const existing = this.getPreferencesFromLocalStorage(userId) || {};
      
      const updatedPreferences = {
        ...existing,
        user_photo: null,
        photo_metadata: null,
        userId,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem(storageKey, JSON.stringify(updatedPreferences));
      // Foto removida do localStorage
      return true;
    } catch (error) {
      // Erro ao remover foto do localStorage
      return false;
    }
  }

  /**
   * Resetar preferências para padrão no localStorage (fallback)
   * @param {string} userId - ID do usuário
   * @returns {boolean} Sucesso da operação
   */
  resetToDefaultInLocalStorage(userId) {
    try {
      const storageKey = `user_preferences_${userId}`;
      const defaultPreferences = {
        userId,
        theme_mode: 'default',
        additional_preferences: {},
        user_photo: null,
        photo_metadata: null,
        lastUpdated: new Date().toISOString(),
        source: 'localStorage',
        version: '1.0.0'
      };
      
      localStorage.setItem(storageKey, JSON.stringify(defaultPreferences));
      // Preferências resetadas no localStorage
      return true;
    } catch (error) {
      // Erro ao resetar preferências no localStorage
      return false;
    }
  }
}

// Instância singleton do serviço
const userPreferencesService = new UserPreferencesService();

export default userPreferencesService;


